import React, { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BlockInteractions,
  Views as BlockInteractionsViews,
  createButtonObject,
  DISPLAY_TYPE,
} from '../../components/organisms/BlockInteractions';
import { Icons } from '../../components/atoms/SVG';
import { Namespace } from '../../translation/namespace';
import { BlockType } from '../../types/api/ElementType';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { ImageGalleryEditView } from './views/ImageGalleryEditView';
import { DefaultView, DeleteAction, OptionalEditView, Views } from '../../types/BlockWithViews';
import { ImageGalleryDefaultView } from './views/ImageGalleryDefaultView';
import { useContentWrapperContext } from '../../components/blocks/ContentWrapper/ContentWrapperContext';
import { useContentWrapperDndContext } from '../../components/blocks/ContentWrapper/ContentWrapperDndProvider';
import { headlineHierarchy } from '../../components/organisms/BlockInteractions/BlockInteractions.utils';

export type ImageGalleryBlockProps = Views<
  DefaultView<ImageGalleryDefaultView> & OptionalEditView<ImageGalleryEditView> & DeleteAction
>;

function ImageGallery({ activeView, views }: ImageGalleryBlockProps) {
  const { t } = useTranslation(Namespace.ImageGallery);
  const { uuid, elementType } = useBlockContext();
  const contentWrapperContext = useContentWrapperContext();
  const contentWrapperDndContext = useContentWrapperDndContext();
  const { refresh, refreshCount } = useRefreshBlock();
  const createButtonsObject = () => {
    const EditViewButton =
      views.edit &&
      createButtonObject(
        BlockInteractionsViews.EDIT,
        Icons.EDIT_PENCIL,
        `${Namespace.ImageGallery}:edit`,
        DISPLAY_TYPE.COLLAPSIBLE,
        <ImageGalleryEditView {...views.edit} />,
      );

    // no need for a view, as delete view is standard
    const DeleteViewButton = createButtonObject(
      BlockInteractionsViews.DELETE,
      Icons.ERASER,
      `${Namespace.ImageGallery}:delete`,
      DISPLAY_TYPE.MODAL,
      null,
    );

    return { ...EditViewButton, ...DeleteViewButton };
  };

  return (
    <BlockInteractions
      uuid={uuid}
      elementType={elementType}
      headline={{
        text: t(`blockHeadline`),
        hierarchy: headlineHierarchy(2, contentWrapperContext?.parentIndex, contentWrapperContext?.grandParentIndex),
        visible: true,
      }}
      views={views}
      activeView={activeView}
      buttons={createButtonsObject()}
      contentWrapperContext={contentWrapperContext}
      contentWrapperDndContext={contentWrapperDndContext}
      refreshBlock={refresh}
      refreshCount={refreshCount}
    >
      <ImageGalleryDefaultView {...views.default} />
    </BlockInteractions>
  );
}

export const ImageGalleryBlock = asFetchableBlock(ImageGallery, BlockType.imagegallery);
export type ImageGalleryBlock = ComponentProps<typeof ImageGalleryBlock>;
